/*
 * THE GO-LIVE RIBBON — the pre-handoff twin of InServiceHeader (#435, Vinnie
 * 22 Oct). Once a bounded delivery board is eligible to go live — its go-live has
 * been reached, or all delivery work is done — a calm, persistent band sits at the
 * top of the board with a "Mark it live" button, so a person never has to wait for
 * the one-time prompt or reload to get it back. It escalates amber → red as the
 * go-live runs overdue (the same thresholds the Portfolio reads, goLiveOverdue),
 * and reads a calm "It's ready to go into service" when the work is done ahead of plan.
 *
 * It PROMPTS; it does not mark live itself — the button requests the handoff
 * dialog (the as-delivered review + confirm), which is the one place a board flips.
 */
import { useApp } from '../store';
import Icon from './Icon';
import { humanDay } from '../when';
import { can } from '../permissions';
import { livePhases, phaseShownStatus, dayFromMs, goLiveAnchorDay } from '../phases';
import { handoffReadiness, handoffOffered, goLiveOverdue } from '../handoff';

export default function GoLiveRibbon() {
  const meta = useApp((s) => s.meta);
  const projectId = useApp((s) => s.projectId);
  const project = useApp((s) => s.projects.find((p) => p.id === s.projectId));
  const phases = useApp((s) => s.phases);
  const issues = useApp((s) => s.issues);
  const milestones = useApp((s) => s.milestones);
  const role = useApp((s) => s.shownRole);
  const setHandoffRequest = useApp((s) => s.setHandoffRequest);

  const now = Date.now();
  const livePh = livePhases(phases);
  // Done-ness is the SHOWN status (override, else card-derived) — the same rule
  // the Plan, Portfolio and HandoffPrompt read, so eligibility can never disagree.
  const derivedPhases = livePh.map((p) => ({ status: phaseShownStatus(p, issues) }));
  const tracked =
    project != null && (project.goLive != null || livePh.length > 0 || project.budget != null);

  // The go-live the ribbon measures against is the ANCHOR-resolved day (0217), not the raw
  // typed date — so a phase/milestone-anchored board is judged by the date the plan sets.
  const goLiveDay = project
    ? goLiveAnchorDay(project, livePh, (id) => milestones.find((m) => m.id === id)?.targetDay)
    : undefined;
  const plannedMs = goLiveDay ? Date.parse(`${goLiveDay}T00:00:00Z`) : project?.goLive;

  const readiness = handoffReadiness({
    deliveryMode: meta.deliveryMode,
    tracked,
    actualGoLive: meta.actualGoLive,
    plannedGoLive: plannedMs,
    phases: derivedPhases,
    nowMs: now,
  });

  // Offered to a manager-and-up only (same right the handoff dialog needs), and
  // only while the board is eligible — never once it is already in service.
  if (!handoffOffered(readiness) || !can(role, 'editSettings')) return null;

  const overdue = goLiveOverdue({
    deliveryMode: meta.deliveryMode,
    tracked,
    actualGoLive: meta.actualGoLive,
    plannedGoLive: plannedMs,
    nowMs: now,
  });
  const planned = plannedMs ?? undefined;

  let tone: 'amber' | 'red' | 'soon' = 'soon';
  let msg: React.ReactNode;
  if (overdue) {
    tone = overdue.level;
    msg =
      overdue.days <= 0 ? (
        <>This board has reached its Go-Live{planned != null ? <> (<b>{humanDay(dayFromMs(planned))}</b>)</> : null} — It's ready to go into service.</>
      ) : (
        <>Go-Live passed <b>{overdue.days} {overdue.days === 1 ? 'day' : 'days'} ago</b>{readiness.state === 'provisional' && readiness.reason === 'phase-open' && livePh.length > 0 ? ' · a delivery phase is still open' : ''} — It's ready to go into service.</>
      );
  } else {
    // before-date: all delivery work is done ahead of the planned Go-Live.
    msg = (
      <>All delivery work is done{planned != null ? <> ahead of the planned Go-Live (<b>{humanDay(dayFromMs(planned))}</b>)</> : null} — It's ready to go into service.</>
    );
  }

  return (
    <div className={`golive-ribbon tone-${tone}`} role="status">
      <Icon name={overdue ? 'warning' : 'check'} size={16} />
      <span className="golive-msg">{msg}</span>
      <button type="button" className="golive-btn" onClick={() => setHandoffRequest(projectId)}>
        Mark it Live
      </button>
    </div>
  );
}
